import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  Buildings, ArrowRight, Warning, PenNib, CurrencyDollar, UsersThree, Trash, Sparkle, CircleNotch,
  EnvelopeSimple, Bank, CreditCard, Plus, Receipt, Briefcase, ChartLineUp, CheckCircle,
  ArrowLeft,
} from "@phosphor-icons/react";

const money = (n) => `$${(n || 0).toLocaleString()}`;
const TYPE_FORM = { Individual: "1040", Partnership: "1065", LLC: "1065", "S-Corp": "1120-S", "C-Corp": "1120", "Non-profit": "990", Trust: "1041" };

// Entity buckets for FR-02 segmented switcher.
// Personal = Individual/Trust · Businesses = LLC/S-Corp/C-Corp/Partnership · Foundations = Non-profit.
const ENTITY_BUCKETS = {
  All: () => true,
  Personal: (t) => t === "Individual" || t === "Trust",
  Businesses: (t) => t === "LLC" || t === "S-Corp" || t === "C-Corp" || t === "Partnership",
  Foundations: (t) => t === "Non-profit",
};
const BUCKET_ORDER = ["All", "Personal", "Businesses", "Foundations"];

// Milestone stepper (FR-04). Portal engagements expose milestone/milestones_total
// as an integer 0..4 from the backend portfolio route. We map that 0..4 int into
// four visual stages so the client sees exactly where their return sits.
const MILESTONES = [
  { key: "intake", label: "Intake" },
  { key: "review", label: "Review" },
  { key: "sign", label: "Sign" },
  { key: "efile", label: "E-File" },
];

// New Service Request modal tiles (FR-03). Order matches the spec verbatim.
const REQUEST_TILES = [
  {
    key: "tax-advisory",
    title: "Tax Advisory & Preparation",
    tagline: "New tax year, extension, quarterly estimates, or IRS notice response",
    Icon: Receipt,
    accent: "from-indigo-500 to-blue-500",
    fields: [
      { name: "tax_year", label: "Tax year / matter", placeholder: "e.g. 2025 return, IRS CP2000 notice", required: true },
      { name: "return_type", label: "Return / form type (if known)", placeholder: "1040, 1120-S, 990, notice response…", required: false },
    ],
  },
  {
    key: "fractional-cfo",
    title: "Fractional CFO & Advisory",
    tagline: "Cash-flow forecasting, board reporting, KPI dashboards",
    Icon: ChartLineUp,
    accent: "from-emerald-500 to-teal-500",
    fields: [
      { name: "monthly_revenue", label: "Approx. monthly revenue", placeholder: "$250K", required: false },
      { name: "priority", label: "Top priority for the first 90 days", placeholder: "Forecast, fundraise readiness, close speed…", required: true },
    ],
  },
  {
    key: "business-formation",
    title: "Entity Formation & Compliance",
    tagline: "LLC / S-Corp / C-Corp / Partnership with EIN + docs",
    Icon: Briefcase,
    accent: "from-amber-500 to-orange-500",
    fields: [
      { name: "preferred_name", label: "Preferred entity name(s) — top 3", placeholder: "Acme Ventures LLC, …", required: true },
      { name: "state", label: "State of formation", placeholder: "TX", required: true },
    ],
  },
];

// -------------------- Access dialog (existing) --------------------
function AccessDialog({ entity, open, onOpenChange }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const { data: grants } = useQuery({
    queryKey: ["entity-access", entity.id],
    queryFn: () => api.get(`/portfolio/access/${entity.id}`).then((r) => r.data),
    enabled: open,
  });
  const grant = useMutation({
    mutationFn: () => api.post("/portfolio/access", { client_id: entity.id, email }),
    onSuccess: () => { toast.success(`Partner access granted — they see ONLY ${entity.name}`); setEmail(""); qc.invalidateQueries({ queryKey: ["entity-access", entity.id] }); },
    onError: (e) => toast.error(errText(e)),
  });
  const revoke = useMutation({
    mutationFn: (grantId) => api.delete(`/portfolio/access/${grantId}`),
    onSuccess: () => { toast.success("Access revoked"); qc.invalidateQueries({ queryKey: ["entity-access", entity.id] }); },
    onError: (e) => toast.error(errText(e)),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="access-dialog">
        <DialogHeader><DialogTitle className="font-heading">Partner access — {entity.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-500">Grant a business partner access to this entity only. They will never see your other entities or personal 1040 documents.</p>
        <div className="flex gap-2">
          <Input data-testid="access-email-input" placeholder="partner@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-8 text-xs" />
          <Button data-testid="access-grant-btn" size="sm" className="h-8" disabled={!email || grant.isPending} onClick={() => grant.mutate()}>Grant</Button>
        </div>
        <div className="space-y-1.5">
          {(grants || []).map((g) => (
            <div key={g.id} className="flex items-center justify-between text-xs border border-zinc-200 rounded-md px-2.5 py-1.5" data-testid={`access-grant-${g.id}`}>
              <span className="text-zinc-700">{g.user_name} <span className="text-zinc-400 font-mono text-[10px]">{g.user_email}</span></span>
              <button data-testid={`access-revoke-btn-${g.id}`} onClick={() => revoke.mutate(g.id)} className="text-zinc-400 hover:text-rose-600 transition-colors duration-150"><Trash size={13} /></button>
            </div>
          ))}
          {!grants?.length && <p className="text-xs text-zinc-400 italic">No partners have access yet.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// -------------------- New Service Request modal (FR-03) --------------------
function NewServiceRequestModal({ open, onOpenChange, entities }) {
  const qc = useQueryClient();
  const [step, setStep] = useState("pick"); // pick | form
  const [tile, setTile] = useState(null);
  const [entityId, setEntityId] = useState("");
  const [notes, setNotes] = useState("");
  const [fieldValues, setFieldValues] = useState({});

  const reset = () => {
    setStep("pick"); setTile(null); setEntityId(""); setNotes(""); setFieldValues({});
  };

  const submit = useMutation({
    mutationFn: () => api.post("/portal/service-requests", {
      service_key: tile.key,
      entity_id: entityId || null,
      notes,
      details: fieldValues,
    }),
    onSuccess: () => {
      toast.success(`Request submitted — your CPA will confirm ${tile.title} within one business day`);
      qc.invalidateQueries({ queryKey: ["portal-service-requests"] });
      reset(); onOpenChange(false);
    },
    onError: (e) => toast.error(errText(e)),
  });

  const canSubmit = tile && (tile.fields.every((f) => !f.required || (fieldValues[f.name] || "").trim()));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="max-w-2xl" aria-describedby={undefined} data-testid="new-request-modal">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {step === "form" && (
              <button data-testid="new-request-back-btn" onClick={() => { setStep("pick"); setTile(null); }} className="text-zinc-400 hover:text-zinc-900 transition-colors">
                <ArrowLeft size={16} />
              </button>
            )}
            {step === "pick" ? "Request a new Artha service" : `Tell us about your ${tile.title}`}
          </DialogTitle>
        </DialogHeader>

        {step === "pick" && (
          <>
            <p className="text-xs text-zinc-500 -mt-2">Pick a category — your CPA will scope and confirm within one business day.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {REQUEST_TILES.map((t) => {
                const Icon = t.Icon;
                return (
                  <button
                    key={t.key}
                    data-testid={`request-tile-${t.key}`}
                    onClick={() => { setTile(t); setStep("form"); }}
                    className="group relative overflow-hidden text-left rounded-lg border border-zinc-200 bg-white p-4 hover:border-zinc-900 hover:shadow-sm transition-all duration-200"
                  >
                    <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full bg-gradient-to-br ${t.accent} opacity-10 group-hover:opacity-20 transition-opacity`} />
                    <div className={`inline-flex items-center justify-center w-9 h-9 rounded-md bg-gradient-to-br ${t.accent} text-white mb-2`}>
                      <Icon size={17} weight="fill" />
                    </div>
                    <p className="text-sm font-medium text-zinc-900">{t.title}</p>
                    <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{t.tagline}</p>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {step === "form" && tile && (
          <div className="space-y-3">
            <div className="rounded-md border border-zinc-100 bg-zinc-50 px-3 py-2 text-[11px] text-zinc-600">
              <span className="font-medium text-zinc-800">{tile.title}</span> — {tile.tagline}
            </div>
            {entities?.length > 0 && (
              <div>
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Which entity is this for?</label>
                <select
                  data-testid="request-entity-select"
                  value={entityId}
                  onChange={(e) => setEntityId(e.target.value)}
                  className="w-full h-8 rounded-md border border-zinc-200 bg-white px-2 text-xs text-zinc-800 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                >
                  <option value="">— Not entity-specific / new entity —</option>
                  {entities.map((en) => <option key={en.id} value={en.id}>{en.name} ({en.entity_type})</option>)}
                </select>
              </div>
            )}
            {tile.fields.map((f) => (
              <div key={f.name}>
                <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">
                  {f.label}{f.required && <span className="text-rose-500"> *</span>}
                </label>
                <Input
                  data-testid={`request-field-${f.name}`}
                  placeholder={f.placeholder}
                  value={fieldValues[f.name] || ""}
                  onChange={(e) => setFieldValues({ ...fieldValues, [f.name]: e.target.value })}
                  className="h-8 text-xs"
                />
              </div>
            ))}
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Anything else we should know?</label>
              <Textarea
                data-testid="request-notes-input"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional — timeline, complexity, prior firm, etc."
                className="text-xs min-h-[70px]"
              />
            </div>
            <Button
              data-testid="request-submit-btn"
              className="w-full"
              disabled={!canSubmit || submit.isPending}
              onClick={() => submit.mutate()}
            >
              {submit.isPending ? <CircleNotch size={13} className="animate-spin mr-1" /> : null}
              Submit request
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// -------------------- Sticky Pulse Header (FR-01) --------------------
function PulseHeader({ missingDocsTotal, outstandingTotal, digest, onOpenNewRequest }) {
  const qc = useQueryClient();
  const gen = useMutation({
    mutationFn: () => api.post("/portfolio/digest/generate"),
    onSuccess: ({ data: d }) => { toast.success(d.emailed ? "Briefing ready — a copy was emailed to you" : "This week's briefing is ready"); qc.invalidateQueries({ queryKey: ["portfolio-digest"] }); },
    onError: (e) => toast.error(errText(e)),
  });
  const emailMe = useMutation({
    mutationFn: () => api.post("/portfolio/digest/email"),
    onSuccess: () => toast.success("Digest emailed to you"),
    onError: (e) => toast.error(errText(e)),
  });
  const emailConfigured = !!digest?.email_configured;
  const hasDigest = !!digest?.digest?.content;
  return (
    <div
      data-testid="portfolio-pulse-header"
      className="sticky top-0 z-30 -mx-4 md:-mx-8 px-4 md:px-8 py-3 backdrop-blur-md bg-white/85 border-b border-zinc-200"
    >
      <div className="max-w-6xl mx-auto flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <div className="flex flex-col">
            <span className="text-[9px] uppercase tracking-widest text-zinc-400">Missing docs</span>
            <span data-testid="pulse-missing-docs" className={`font-mono text-lg leading-none ${missingDocsTotal > 0 ? "text-amber-700" : "text-emerald-700"}`}>
              {missingDocsTotal}
            </span>
          </div>
          <div className="w-px h-8 bg-zinc-200" />
          <div className="flex flex-col">
            <span className="text-[9px] uppercase tracking-widest text-zinc-400">Outstanding</span>
            <span data-testid="pulse-outstanding" className={`font-mono text-lg leading-none ${outstandingTotal > 0 ? "text-rose-700" : "text-emerald-700"}`}>
              {money(outstandingTotal)}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasDigest && (
            <Button
              data-testid="pulse-email-briefing"
              size="sm" variant="outline" className="h-8 text-[11px]"
              disabled={!emailConfigured || emailMe.isPending}
              title={emailConfigured ? "Email me this briefing" : "Activates once your firm connects an email key"}
              onClick={() => emailMe.mutate()}
            >
              <EnvelopeSimple size={12} className="mr-1" /> Email briefing
            </Button>
          )}
          <Button
            data-testid="pulse-refresh-briefing"
            size="sm" variant="outline" className="h-8 text-[11px]"
            disabled={gen.isPending}
            onClick={() => gen.mutate()}
          >
            {gen.isPending ? <CircleNotch size={12} className="animate-spin mr-1" /> : <Sparkle size={12} className="mr-1" />}
            {hasDigest ? "Refresh briefing" : "Generate briefing"}
          </Button>
          <Button
            data-testid="pulse-new-request-btn"
            size="sm" className="h-8 text-[11px] bg-zinc-900 hover:bg-zinc-800"
            onClick={onOpenNewRequest}
          >
            <Plus size={12} className="mr-1" /> New service request
          </Button>
        </div>
      </div>
    </div>
  );
}

// -------------------- Weekly digest (unchanged content, un-sticky) --------------------
function WeeklyDigestBody() {
  const { data } = useQuery({ queryKey: ["portfolio-digest"], queryFn: () => api.get("/portfolio/digest").then((r) => r.data) });
  const c = data?.digest?.content;
  if (!c) {
    return (
      <div className="rounded-lg border border-zinc-200 bg-white p-4 mt-5 mb-5" data-testid="weekly-digest-empty">
        <p className="text-sm font-medium text-zinc-900 flex items-center gap-1.5 mb-1"><Sparkle size={14} /> Weekly portfolio briefing</p>
        <p className="text-[11px] text-zinc-500">A plain-English note covering every entity — return status, missing docs, and balances — refreshed weekly.{!data?.email_configured && " Email delivery switches on once your firm connects an email key."}</p>
      </div>
    );
  }
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5 mt-5 mb-5" data-testid="weekly-digest">
      <p className="text-sm font-medium text-zinc-900 flex items-center gap-1.5 mb-2"><Sparkle size={14} /> Weekly portfolio briefing</p>
      <div className="space-y-2.5" data-testid="digest-content">
        <p className="text-sm font-medium text-zinc-900">{c.headline}</p>
        <p className="text-xs text-zinc-600">{c.overview}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
          {(c.entities || []).map((e, i) => (
            <p key={`digest-e-${i}-${e.name}`} className="text-[11px] text-zinc-700 bg-zinc-50 border border-zinc-100 rounded-md px-2.5 py-1.5"><span className="font-medium">{e.name}</span> — {e.status_line}</p>
          ))}
        </div>
        {(c.action_items || []).length > 0 && (
          <div>
            <p className="text-[10px] uppercase tracking-wide text-zinc-400 mb-1">This week</p>
            {(c.action_items || []).map((a, i) => <p key={`digest-a-${i}-${typeof a === "string" ? a.slice(0, 20) : i}`} className="text-xs text-zinc-700">→ {a}</p>)}
          </div>
        )}
        <p className="text-[10px] text-zinc-400">{c.closing} · generated {new Date(data.digest.generated_at).toLocaleDateString()}{!data?.fresh && " · refresh for this week's numbers"}</p>
      </div>
    </div>
  );
}

// -------------------- Milestone stepper (FR-04) --------------------
function MilestoneStepper({ milestone, milestonesTotal }) {
  // Backend gives us a 0..N int. Map into 4 visible pips: Intake, Review, Sign, E-File.
  // We assume milestonesTotal >= 4; if smaller, clamp.
  const total = Math.max(milestonesTotal || 4, MILESTONES.length);
  const pct = Math.min(100, Math.round(((milestone || 0) / total) * 100));
  const activeIdx = Math.min(MILESTONES.length - 1, Math.floor((milestone || 0) * MILESTONES.length / total));
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        {MILESTONES.map((m, i) => (
          <div key={m.key} className="flex items-center gap-1">
            <div
              className={`w-2 h-2 rounded-full transition-all duration-300 ${i < activeIdx ? "bg-zinc-900" : i === activeIdx ? "bg-zinc-900 ring-2 ring-zinc-900/20" : "bg-zinc-200"}`}
            />
            <span className={`text-[9px] uppercase tracking-wide ${i <= activeIdx ? "text-zinc-700" : "text-zinc-400"}`}>{m.label}</span>
            {i < MILESTONES.length - 1 && <div className={`w-4 h-px ${i < activeIdx ? "bg-zinc-900" : "bg-zinc-200"}`} />}
          </div>
        ))}
      </div>
      <div className="h-1 rounded-full bg-zinc-100 overflow-hidden">
        <div className="h-full bg-gradient-to-r from-zinc-800 to-zinc-900 rounded-full transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// -------------------- Consolidated billing (FR-06 filter pills) --------------------
const BILLING_FILTERS = [
  { key: "all", label: "All" },
  { key: "unpaid", label: "Unpaid" },
  { key: "paid_ytd", label: "Paid YTD" },
];

function ConsolidatedBilling() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState("all");
  const { data } = useQuery({ queryKey: ["portfolio-invoices"], queryFn: () => api.get("/portfolio/invoices").then((r) => r.data) });
  const pay = useMutation({
    mutationFn: ({ inv, method }) => api.post(`/portal/invoices/${inv.id}/pay`, { method }, { headers: { "X-Entity-ID": inv.client_id } }),
    onSuccess: () => { toast.success("Payment received — thank you!"); qc.invalidateQueries({ queryKey: ["portfolio-invoices"] }); qc.invalidateQueries({ queryKey: ["portfolio"] }); },
    onError: (e) => toast.error(errText(e)),
  });
  const allInv = useMemo(() => data?.invoices || [], [data]);
  const filtered = useMemo(() => {
    if (filter === "unpaid") return allInv.filter((i) => i.status !== "paid");
    if (filter === "paid_ytd") return allInv.filter((i) => i.status === "paid");
    return allInv;
  }, [allInv, filter]);
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5 mt-6" data-testid="consolidated-billing">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h3 className="text-sm font-medium text-zinc-900">Consolidated statement — all entities</h3>
        <div className="flex items-center gap-4 text-xs">
          <span className="text-rose-700">Outstanding: <span className="font-mono" data-testid="billing-outstanding">{money(data?.outstanding_total)}</span></span>
          <span className="text-zinc-500">Paid YTD: <span className="font-mono">{money(data?.paid_ytd)}</span></span>
        </div>
      </div>
      <div className="flex items-center gap-1 mb-3">
        {BILLING_FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`billing-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-all duration-150 ${filter === f.key ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}
          >
            {f.label}
            {f.key === "unpaid" && allInv.filter((i) => i.status !== "paid").length > 0 && (
              <span className="ml-1 text-[9px] opacity-80">({allInv.filter((i) => i.status !== "paid").length})</span>
            )}
          </button>
        ))}
      </div>
      <div className="space-y-1.5">
        {filtered.map((i) => (
          <div key={i.id} data-testid={`portfolio-invoice-${i.id}`} className="flex items-center justify-between gap-3 rounded-md border border-zinc-100 px-3 py-2">
            <div className="min-w-0">
              <p className="text-xs text-zinc-800 truncate"><span className="font-mono">{i.number}</span> · {i.entity_name} <span className="text-zinc-400">— {i.description}</span></p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="font-mono text-xs text-zinc-900">{money(i.amount)}</span>
              {i.status === "paid" ? (
                <span className="text-[9px] uppercase bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-sm px-1.5 py-0.5">Paid</span>
              ) : (
                <>
                  <Button data-testid={`pf-pay-ach-${i.id}`} size="sm" variant="outline" className="h-6 text-[10px] px-2" disabled={pay.isPending} onClick={() => pay.mutate({ inv: i, method: "ach" })}><Bank size={11} className="mr-0.5" /> ACH</Button>
                  <Button data-testid={`pf-pay-card-${i.id}`} size="sm" className="h-6 text-[10px] px-2" disabled={pay.isPending} onClick={() => pay.mutate({ inv: i, method: "card" })}><CreditCard size={11} className="mr-0.5" /> Card</Button>
                </>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-xs text-zinc-400 italic">Nothing to show for this filter.</p>}
      </div>
    </div>
  );
}

// -------------------- Root --------------------
export function PortfolioDashboard({ portfolio, onSelect }) {
  const [accessEntity, setAccessEntity] = useState(null);
  const [newReqOpen, setNewReqOpen] = useState(false);
  const [bucket, setBucket] = useState("All");
  const entities = useMemo(() => portfolio?.entities || [], [portfolio]);

  const { data: digest } = useQuery({ queryKey: ["portfolio-digest"], queryFn: () => api.get("/portfolio/digest").then((r) => r.data) });

  // Aggregate counters for the pulse header.
  const missingDocsTotal = useMemo(() => entities.reduce((s, e) => s + (e.missing_docs || 0), 0), [entities]);
  const outstandingTotal = useMemo(() => entities.reduce((s, e) => s + (e.unpaid_amount || 0), 0), [entities]);

  // Bucket-filtered entities for the grid.
  const shown = useMemo(() => entities.filter((en) => ENTITY_BUCKETS[bucket](en.entity_type)), [entities, bucket]);

  // Per-bucket count badges next to each pill.
  const bucketCount = (b) => entities.filter((en) => ENTITY_BUCKETS[b](en.entity_type)).length;

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-8 fade-up" data-testid="portfolio-dashboard">
      <PulseHeader
        missingDocsTotal={missingDocsTotal}
        outstandingTotal={outstandingTotal}
        digest={digest}
        onOpenNewRequest={() => setNewReqOpen(true)}
      />

      <div className="pt-5">
        <h1 className="font-heading text-2xl font-medium tracking-tight text-zinc-900">Your entity portfolio</h1>
        <p className="text-sm text-zinc-500 mt-1">One login, every return — personal, businesses, and foundations in a single pane of glass.</p>
      </div>

      <WeeklyDigestBody />

      {/* FR-02 entity switcher pills */}
      <div className="flex items-center gap-1.5 mb-4" data-testid="entity-switcher">
        {BUCKET_ORDER.map((b) => {
          const n = bucketCount(b);
          const active = bucket === b;
          return (
            <button
              key={b}
              data-testid={`entity-filter-${b.toLowerCase()}`}
              onClick={() => setBucket(b)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all duration-150 ${active ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"}`}
            >
              {b} <span className={`ml-1 text-[10px] ${active ? "opacity-80" : "text-zinc-400"}`}>{n}</span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {shown.map((en) => {
          const totalDue = en.unpaid_amount || 0;
          const missing = en.missing_docs || 0;
          const pending = en.pending_signatures || 0;
          const statusBadge = missing > 0
            ? { label: "Docs needed", cls: "bg-amber-50 text-amber-800 border-amber-200" }
            : pending > 0
              ? { label: "Ready to sign", cls: "bg-indigo-50 text-indigo-800 border-indigo-200" }
              : totalDue > 0
                ? { label: "Balance due", cls: "bg-rose-50 text-rose-800 border-rose-200" }
                : { label: "All caught up", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" };
          return (
            <div key={en.id} data-testid={`entity-card-${en.id}`} className="rounded-lg border border-zinc-200 bg-white p-5 flex flex-col hover:border-zinc-400 hover:shadow-sm transition-all duration-200">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-md bg-zinc-900 flex items-center justify-center shrink-0"><Buildings size={15} weight="fill" className="text-white" /></div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-zinc-900 truncate">{en.name}</p>
                    <p className="text-[10px] text-zinc-500">{en.entity_type} · Form {TYPE_FORM[en.entity_type] || "—"}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <span data-testid={`entity-status-${en.id}`} className={`text-[9px] uppercase border rounded-sm px-1.5 py-0.5 ${statusBadge.cls}`}>{statusBadge.label}</span>
                  {en.access_role === "partner" && <span className="text-[9px] uppercase bg-indigo-50 text-indigo-700 border border-indigo-200 rounded-sm px-1.5 py-0.5">Partner</span>}
                </div>
              </div>
              <div className="space-y-3 flex-1">
                {en.engagements.map((e) => (
                  <div key={e.id}>
                    <div className="flex items-center justify-between text-[11px] mb-1.5">
                      <span className="text-zinc-700 font-medium">{e.service_type} · {e.tax_year}</span>
                      <span className="text-zinc-400 font-mono tabular-nums">{Math.round((e.milestone / (e.milestones_total || 4)) * 100)}%</span>
                    </div>
                    <MilestoneStepper milestone={e.milestone} milestonesTotal={e.milestones_total} />
                  </div>
                ))}
                {!en.engagements.length && <p className="text-xs text-zinc-400 italic">No active returns</p>}
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 text-[11px]">
                {missing > 0 && <span className="flex items-center gap-1 text-amber-700"><Warning size={12} /> {missing} docs needed</span>}
                {pending > 0 && <span className="flex items-center gap-1 text-indigo-700"><PenNib size={12} /> {pending} to sign</span>}
                {totalDue > 0 && <span className="flex items-center gap-1 text-rose-700"><CurrencyDollar size={12} /> {money(totalDue)} due</span>}
                {!missing && !pending && !totalDue && <span className="flex items-center gap-1 text-emerald-700"><CheckCircle size={12} weight="fill" /> All caught up</span>}
              </div>
              <div className="flex gap-2 mt-4">
                <Button data-testid={`entity-open-btn-${en.id}`} size="sm" className="flex-1 h-8 text-xs" onClick={() => onSelect(en.id)}>
                  Open <ArrowRight size={13} className="ml-1" />
                </Button>
                {en.access_role === "owner" && (
                  <Button data-testid={`entity-access-btn-${en.id}`} size="sm" variant="outline" className="h-8 text-xs" onClick={() => setAccessEntity(en)}>
                    <UsersThree size={13} />
                  </Button>
                )}
              </div>
            </div>
          );
        })}
        {shown.length === 0 && (
          <div className="col-span-full rounded-lg border border-dashed border-zinc-200 bg-white p-8 text-center" data-testid="entity-empty-bucket">
            <p className="text-sm text-zinc-500">No {bucket.toLowerCase()} entities yet.</p>
            <button onClick={() => setBucket("All")} className="text-xs text-zinc-900 underline underline-offset-2 mt-1">Show all entities</button>
          </div>
        )}
      </div>

      {accessEntity && <AccessDialog entity={accessEntity} open={!!accessEntity} onOpenChange={(o) => !o && setAccessEntity(null)} />}
      <ConsolidatedBilling />

      <NewServiceRequestModal open={newReqOpen} onOpenChange={setNewReqOpen} entities={entities} />
    </div>
  );
}
